import React from 'react';

import { Combobox } from '@components/combobox';
import { Api } from '@core/api';
import { injectTSDI } from '@core/tsdi';
import { useDebounce } from '@components/utils/use-debounce';
import { CurrencyDto } from '@api/WalletClient';

interface CurrencySelectProps {
    value: string;
    onChange: (code: string) => void;
    error?: boolean;
    helperText?: string;
}

export const CurrencySelect = ({
    value,
    onChange,
    error,
    helperText
}: CurrencySelectProps) => {
    const { walletClient } = injectTSDI(Api);
    const [options, setOptions] = React.useState<CurrencyDto[]>([]);
    const [loading, setLoading] = React.useState(false);
    const [searchTerm, setSearchTerm] = React.useState('');
    const debouncedSearch = useDebounce(searchTerm, 300);
    const fetchedCodesRef = React.useRef(new Set<string>());

    React.useEffect(() => {
        let active = true;
        setLoading(true);
        walletClient.supportApi
            .currencySupportControllerGetAllCurrencies({
                search: debouncedSearch,
                size: 50
            })
            .then((res) => {
                if (active) setOptions(res.data.content ?? []);
            })
            .catch(() => {
                if (active) setOptions([]);
            })
            .finally(() => {
                if (active) setLoading(false);
            });
        return () => {
            active = false;
        };
    }, [debouncedSearch]);

    // Resolve a pre-selected code once; `options` stays out of the deps to avoid stale closures.
    React.useEffect(() => {
        if (!value || fetchedCodesRef.current.has(value)) return;
        fetchedCodesRef.current.add(value);
        let active = true;
        walletClient.supportApi
            .currencySupportControllerGetAllCurrencies({
                search: value,
                size: 10
            })
            .then((res) => {
                if (!active) return;
                const incoming = res.data.content ?? [];
                setOptions((prev) => {
                    const merged = [...prev, ...incoming];
                    return merged.filter(
                        (c, i, arr) =>
                            arr.findIndex((x) => x.code === c.code) === i
                    );
                });
            });
        return () => {
            active = false;
        };
    }, [value]);

    const selectedOption = options.find((c) => c.code === value) ?? null;

    return (
        <Combobox<CurrencyDto>
            label="Currency"
            options={options}
            value={selectedOption}
            onChange={(option) => onChange(option?.code ?? '')}
            onInputChange={setSearchTerm}
            getOptionLabel={(option) => `${option.name} (${option.code})`}
            getOptionKey={(option) => option.code}
            loading={loading}
            error={error ? (helperText ?? 'Invalid currency') : undefined}
            helperText={error ? undefined : helperText}
        />
    );
};
